import React, {
  FC,
  useEffect,
  useState,
  useCallback,
  useRef,
  forwardRef
} from "react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Asset, Entry } from "contentful";
import { Document } from "@contentful/rich-text-types";
import CTA, { CTAProps } from "../CTA";
import {
  Figure,
  BackgroundContainer,
  Video,
  Image,
  ButtonContainer,
  Caption,
  LogoImage,
  ContentWrapper,
} from "./styles";

export interface HeroModuleProps extends Entry {
  id: string;
  brand: string;
  parentProps: any;
  fields: {
    mainMedia?: Asset;
    mobileMedia?: Asset;
    altText?: string;
    logoImage?: Asset;
    logoMaxWidth?: number;
    textContent?: Document;
    contentAlignment?: string;
    mobileContentAlignment?: string;
    contentMaxWidth?: number;
    theme?: string;
    mobileTheme?: string;
    useBackgroundOverlay?: boolean;
    ctas?: CTAProps[];
    showBehindNavigation?: boolean;
    disableLogoFilter?: boolean;
    backgroundPosition?: string;
    heroHeight?: string;
    belowSectionId?: string;
  };
}

const RenderedAsset: FC<{
  asset: Asset;
  altText?: string;
  objectPosition?: string;
}> = ({ asset, altText, objectPosition }) => {
  const contentType = asset.fields.file?.contentType as string;
  let url = `https:${asset.fields.file?.url}`;

  if (contentType.includes("video")) {
    return (
      <Video autoPlay loop muted playsInline $objectPosition={objectPosition}>
        <source src={url} type={contentType} />
      </Video>
    );
  } else {
    url = `https://www.fender.com/cdn-cgi/image/format=webp,resize=height=auto,width=2048/https:${asset.fields.file?.url}`;
  }

  return <Image src={url} alt={altText} $objectPosition={objectPosition} />;
};

interface ContentMarkupProps {
  brand: string;
  fields: HeroModuleProps['fields'];
  isMobile: boolean;
  activeTheme: string;
}

// forwardRef so we can measure this wrapper directly
const ContentMarkup = forwardRef<HTMLDivElement, ContentMarkupProps>(
  ({ brand, fields, isMobile, activeTheme }, ref) => {
    const {
      contentAlignment,
      contentMaxWidth,
      disableLogoFilter,
      mobileContentAlignment,
      logoImage,
      logoMaxWidth,
      textContent,
      ctas,
    } = fields;

    return (
      <ContentWrapper
        ref={ref}
        $contentAlignment={contentAlignment}
        $isMobile={isMobile}
        $maxWidth={contentMaxWidth}
        $mobileContentAlignment={mobileContentAlignment}
        $theme={activeTheme}
        $brand={brand}
      >
        {logoImage && logoImage.fields.file && (
          <LogoImage
            alt="Logo"
            src={`https:${logoImage.fields.file.url}`}
            $maxWidth={logoMaxWidth}
            $theme={activeTheme}
            $disableFilter={disableLogoFilter}
          />
        )}

        {textContent && (
          <Caption
            $contentAlignment={contentAlignment}
            $isMobile={isMobile}
            $maxWidth={contentMaxWidth}
            $theme={activeTheme}
            $brand={brand}
            $mobileContentAlignment={mobileContentAlignment}
          >
            {documentToReactComponents(textContent)}
            {ctas && (
              <ButtonContainer
                $contentAlignment={contentAlignment}
                $theme={activeTheme}
              >
                {ctas.map((cta, i) => (
                  <CTA
                    key={i}
                    {...cta}
                    brand={brand}
                  />
                ))}
              </ButtonContainer>
            )}
          </Caption>
        )}
      </ContentWrapper>
    );
  }
);

const HeroModule: FC<HeroModuleProps> = ({ id, brand, parentProps, fields }) => {
  const {
    mainMedia,
    mobileMedia,
    altText,
    contentAlignment,
    mobileContentAlignment,
    theme: moduleTheme,
    mobileTheme: moduleMobileTheme,
    useBackgroundOverlay,
    showBehindNavigation = false,
    backgroundPosition,
  } = fields;
  const parentTheme = parentProps?.theme;
  const desktopTheme = moduleTheme ?? parentTheme;
  const mobileTheme = moduleMobileTheme ?? moduleTheme ?? parentTheme;

  const [isMobile, setIsMobile] = useState(false);
  const handleResize = useCallback(() => {
    setIsMobile(window.innerWidth < 1024);
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const activeTheme = isMobile ? mobileTheme : desktopTheme;
  const mediaToShow = isMobile ? mobileMedia || mainMedia : mainMedia;

  const heroRef = useRef<HTMLDivElement>(null);
  // ref for the content wrapper so we can measure it
  const contentRef = useRef<HTMLDivElement>(null);

  const [dynamicHeight, setDynamicHeight] = useState<string | undefined>();

  useEffect(() => {
    const getTotalNavHeight = () => {
      let total = 56; // marketingPage top padding
      const header = document.querySelector('.section-header') as HTMLElement | null;
  
      if (header) total += header.offsetHeight;
      return total;
    };

    const updateHeroHeight = () => {
      const navOffset = getTotalNavHeight();  
      const belowId = fields.belowSectionId;
      const belowEl = belowId ? document.getElementById(belowId) : null;
      const belowOffset = belowEl?.offsetHeight ?? 0;
    
      let heightCalc: string | undefined;
    
      switch (fields.heroHeight) {
        case 'Show Section Below':
          heightCalc = `calc(100vh - ${navOffset + belowOffset}px)`;
          break;
        case '90 Percent':
          heightCalc = `calc(90vh - ${navOffset}px)`;
          break;
        case '100 Percent':
          heightCalc = `calc(100vh - ${navOffset}px)`;
          break;
        default:
          heightCalc = undefined;
      }
    
      setDynamicHeight(heightCalc);
    };
  
    requestAnimationFrame(() => {
      updateHeroHeight();
      window.addEventListener('resize', updateHeroHeight);
    });
  
    return () => window.removeEventListener('resize', updateHeroHeight);
  }, [fields.heroHeight]);

  let extraHeight = 0;
  if (showBehindNavigation) {
    const headerWrapperDiv = document.getElementsByClassName('header-wrapper');
    const utilityBar = document.getElementsByClassName('utility-bar');

    if (headerWrapperDiv) {
      // @ts-ignore
      headerWrapperDiv[0].style.background = 'transparent';
      // @ts-ignore
      extraHeight += headerWrapperDiv[0].offsetHeight;
    }

    if (utilityBar) {
      // @ts-ignore
      utilityBar[0].style.background = 'transparent';
      // @ts-ignore
      extraHeight += utilityBar[0].offsetHeight;
    }
  }

  return (
    <>
      <Figure
        ref={heroRef}
        data-component-id={id}
        $contentAlignment={contentAlignment || 'center'}
        $isMobile={isMobile}
        $mobileContentAlignment={mobileContentAlignment || 'center'}
        $theme={activeTheme}
        $useBackgroundOverlay={!!useBackgroundOverlay}
        $brand={brand}
        $dynamicHeight={dynamicHeight}
      >
        <BackgroundContainer
          $transparentHeaderHeight={extraHeight}
          $useTransparentHeader={showBehindNavigation}
        >
          {mediaToShow && (
            <RenderedAsset
              asset={mediaToShow}
              altText={altText}
              objectPosition={backgroundPosition}
            />
          )}
        </BackgroundContainer>

        {(mobileContentAlignment !== 'below' || !isMobile) && (
          <ContentMarkup
            ref={contentRef}
            brand={brand}
            fields={fields}
            isMobile={isMobile}
            activeTheme={activeTheme}
          />
        )}
      </Figure>

      {(mobileContentAlignment == 'below' && isMobile) && (
        <ContentMarkup
          ref={contentRef}
          brand={brand}
          fields={fields}
          isMobile={isMobile}
          activeTheme={activeTheme}
        />
      )}
    </>
  );
};

export default HeroModule;
